import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Preview as PreviewIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const HRPayrollProcessing = () => {
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [workingDays, setWorkingDays] = useState(30);
  const [otRate, setOtRate] = useState(80);
  const [batchId, setBatchId] = useState(null);
  const [batchCode, setBatchCode] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // ========== GET AUTH TOKEN ==========
  const getAuthToken = () => {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  };

  // ========== GET HEADERS ==========
  const getHeaders = () => {
    const token = getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  // Months list
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  // ========== CREATE BATCH ==========
  const handleCreateBatch = async () => {
    if (!month || !year) {
      setMessage({ type: 'error', text: 'Please select month and year' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/hr/payroll/batch/create`,
        {
          month,
          year,
          total_working_days: workingDays,
          ot_rate: otRate,
        },
        { headers: getHeaders() }
      );

      setBatchId(response.data.data.batch_id);
      setBatchCode(response.data.data.batch_code);
      setMessage({
        type: 'success',
        text: `Batch created successfully! Batch Code: ${response.data.data.batch_code}`
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to create batch'
      });
    } finally {
      setLoading(false);
    }
  };

  // ========== DOWNLOAD TEMPLATE ==========
  const handleDownloadTemplate = async () => {
    if (!batchId) {
      setMessage({ type: 'error', text: 'Please create a batch first' });
      return;
    }

    setLoading(true);

    try {
      const response = await axios({
        method: 'get',
        url: `${API_URL}/api/hr/payroll/batch/${batchId}/template`,
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_template_${month}_${year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setMessage({ type: 'success', text: 'Template downloaded successfully!' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to download template'
      });
    } finally {
      setLoading(false);
    }
  };

  // ========== UPLOAD TEMPLATE ==========
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setMessage({ type: 'info', text: `File selected: ${selectedFile.name}` });
    }
  };

  const handleUploadTemplate = async () => {
    if (!batchId) {
      setMessage({ type: 'error', text: 'Please create a batch first' });
      return;
    }

    if (!file) {
      setMessage({ type: 'error', text: 'Please select a file to upload' });
      return;
    }

    setLoading(true);
    setMessage(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API_URL}/api/hr/payroll/batch/${batchId}/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      setMessage({
        type: 'success',
        text: response.data.message
      });
      setPreviewData(response.data.data);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to upload template'
      });
    } finally {
      setLoading(false);
    }
  };

  // ========== PREVIEW ==========
  const handlePreview = async () => {
    if (!batchId) {
      setMessage({ type: 'error', text: 'Please create a batch first' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await axios.get(
        `${API_URL}/api/hr/payroll/batch/${batchId}/preview`,
        { headers: getHeaders() }
      );
      
      setPreviewData(response.data.data);
      setPreviewOpen(true);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to generate preview'
      });
    } finally {
      setLoading(false);
    }
  };

  // ========== SUBMIT TO FINANCE ==========
  const handleSubmitToFinance = async () => {
    if (!batchId) {
      setMessage({ type: 'error', text: 'Please create a batch first' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/hr/payroll/batch/${batchId}/submit`,
        {},
        { headers: getHeaders() }
      );
      
      setMessage({
        type: 'success',
        text: response.data.message || 'Payroll submitted to finance successfully!'
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to submit to finance'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: 4, width: '100%' }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        HR Payroll Processing
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      {/* Step 1: Create Batch */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Step 1: Create Payroll Batch
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                select
                fullWidth
                label="Month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                SelectProps={{ native: true }}
                 InputLabelProps={{
      shrink: true,
    }}
              >
                <option value="">Select Month</option>
                {months.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                fullWidth
                label="Year"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                SelectProps={{ native: true }}
                 InputLabelProps={{
      shrink: true,
    }}
              >
                <option value="">Select Year</option>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                type="number"
                label="Working Days"
                value={workingDays}
                onChange={(e) => setWorkingDays(Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                type="number"
                label="OT Rate (₹)"
                value={otRate}
                onChange={(e) => setOtRate(Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleCreateBatch}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Create Batch'}
              </Button>
            </Grid>
          </Grid>

          {batchId && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="body2">
                ✅ Batch Created: <strong>{batchCode}</strong> (ID: {batchId})
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Step 2: Download Template */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Step 2: Download & Fill Template
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Download the Excel template, fill in LOP days, OT hours, Advance Salary, and Reimbursements.
            Worked days are auto-filled from attendance data.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            disabled={!batchId || loading}
          >
            Download Template
          </Button>
        </CardContent>
      </Card>

      {/* Step 3: Upload Template */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Step 3: Upload Filled Template
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                style={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<UploadIcon />}
                onClick={handleUploadTemplate}
                disabled={!batchId || !file || loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Upload'}
              </Button>
            </Grid>
          </Grid>

          {previewData && previewData.employees && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                {previewData.total_employees} employees processed successfully!
              </Alert>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee ID</TableCell>
                      <TableCell>Worked Days</TableCell>
                      <TableCell>LOP Days</TableCell>
                      <TableCell>OT Hours</TableCell>
                      <TableCell>Advance (₹)</TableCell>
                      <TableCell>Reimbursements (₹)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.employees.slice(0, 5).map((emp) => (
                      <TableRow key={emp.employee_id}>
                        <TableCell>{emp.employee_id}</TableCell>
                        <TableCell>{emp.worked_days}</TableCell>
                        <TableCell>{emp.lop_days}</TableCell>
                        <TableCell>{emp.ot_hours}</TableCell>
                        <TableCell>₹{emp.advance_salary}</TableCell>
                        <TableCell>₹{emp.reimbursements}</TableCell>
                      </TableRow>
                    ))}
                    {previewData.employees.length > 5 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          ... and {previewData.employees.length - 5} more employees
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Step 4: Preview & Submit */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Step 4: Preview & Submit to Finance
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Preview shows ONLY the data you entered. All financial calculations will be done by Finance team after approval.
          </Typography>
          <Grid container spacing={2}>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={handlePreview}
                disabled={!batchId || loading}
              >
                Preview Data
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="success"
                startIcon={<SendIcon />}
                onClick={handleSubmitToFinance}
                disabled={!batchId || loading}
              >
                Submit to Finance
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Preview - HR Payroll Data
          {previewData && (
            <Chip 
              label={`Batch: ${previewData.batch_code}`} 
              size="small" 
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {previewData && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                This preview shows ONLY the data you entered. All financial calculations 
                (LOP deduction, OT amount, PF, Tax, Net Payable) will be done by Finance team after approval.
              </Alert>

              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Worked Days</TableCell>
                      <TableCell>LOP Days</TableCell>
                      <TableCell>OT Hours</TableCell>
                      <TableCell>OT Rate</TableCell>
                      <TableCell>Advance (₹)</TableCell>
                      <TableCell>Reimbursements (₹)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.employees?.map((emp) => (
                      <TableRow key={emp.employee_id}>
                        <TableCell>{emp.employee_id}</TableCell>
                        <TableCell>{emp.name}</TableCell>
                        <TableCell>{emp.worked_days}</TableCell>
                        <TableCell>{emp.lop_days}</TableCell>
                        <TableCell>{emp.ot_hours}</TableCell>
                        <TableCell>₹{emp.ot_rate}</TableCell>
                        <TableCell>₹{emp.advance_salary}</TableCell>
                        <TableCell>₹{emp.reimbursements}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Summary:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2">
                      <strong>Total Employees:</strong> {previewData.summary?.total_employees || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2">
                      <strong>Total LOP Days:</strong> {previewData.summary?.total_lop_days || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2">
                      <strong>Total OT Hours:</strong> {previewData.summary?.total_ot_hours || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2">
                      <strong>Total Advance:</strong> ₹{previewData.summary?.total_advance || 0}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HRPayrollProcessing;